import { isMobile } from '@/utils/DeviceUtility'
import { uniq } from 'lodash'
import { logsWidgetMappings, operationalWidgetMappings, byproductsWidgetMappings } from '@/components/dashboard/WidgetMappings'

export const NOT_SET = 'NOT_SET'

const widgetMappings = [
  ...logsWidgetMappings,
  ...operationalWidgetMappings,
  ...byproductsWidgetMappings
]

export const SETTING_TYPES = {
  CONTRACT_FORM: 'contractFormSettings',
  ACTIVITY_FORM: 'activityFormSettings',
  TRACT_FORM: 'tractFormSettings',
  TABLE: 'tableSettings',
  DASHBOARD: 'dashboardSettings',
  AUTOCOMPLETE: 'autocompleteSettings'
}

const getSettingKeyForDevice = (string, isMobile) => {
  const mobileExtension = 'Mobile'
  return isMobile ? string.concat(mobileExtension) : string
}

const DeviceType = {
  CURRENT: 'current',
  MOBILE: 'mobile',
  DESKTOP: 'desktop'
}

const getListOfSettingKeysByDeviceType = (deviceType = DeviceType.CURRENT) => {
  let getter = getSettingKeyForDevice
  switch (deviceType) {
    case DeviceType.MOBILE:
      getter = (key) => getSettingKeyForDevice(key, true)
      break
    case DeviceType.DESKTOP:
      getter = (key) => getSettingKeyForDevice(key, false)
      break
    default:
      getter = (key) => getSettingKeyForDevice(key, isMobile())
  }

  return getSettingKeys(getter)
}

function getSettingKeys (dynamicKeyGetter) {
  const userSettingKeysForDashboardWidgets = getUserSettingKeysForDashboardWidgets(dynamicKeyGetter)
  const userSettingKeys = getUserSettingKeys(dynamicKeyGetter)

  return {
    ...userSettingKeys,
    ...userSettingKeysForDashboardWidgets
  }
}

function getUserSettingKeysForDashboardWidgets (dynamicKeyGetter) {
  const keysFromWidgets = {}
  const widgetsForKeys = widgetMappings.filter(widget => !!widget.userSettingKey)

  widgetsForKeys.forEach(widget => {
    Object.defineProperty(keysFromWidgets, widget.userSettingKey.key, {
      get: () => { return dynamicKeyGetter(widget.userSettingKey.value) },
      enumerable: true,
      configurable: true
    })
  })

  return keysFromWidgets
}

function getUserSettingKeys (dynamicKeyGetter) {
  return {
    ACCOUNT_AUTOCOMPLETE: 'accountAutocompleteTags',
    DESTINATION_AUTOCOMPLETE: 'destinationAutocompleteTags',
    FROM_ACCOUNT_AUTOCOMPLETE: 'fromAccountAutocompleteTags',
    DEFAULT_ACCOUNT_AUTOCOMPLETE: 'defaultAccountAutocomplete',
    LAND_OWNER_ACCOUNT_AUTOCOMPLETE: 'landOwnerAccountAutocomplete',
    DRIVER_ACCOUNT_AUTOCOMPLETE: 'driverAccountAutocomplete',
    HAULER_ACCOUNT_AUTOCOMPLETE: 'haulerAccountAutocomplete',
    SUPPLIER_ACCOUNT_AUTOCOMPLETE: 'supplierAccountAutocomplete',
    TRACT_CONTRACT_ACCOUNT_AUTOCOMPLETE: 'tractContractAccountAutocomplete',
    TRACT_DESTINATION_AUTOCOMPLETE: 'tractDestinationAutocomplete',
    ACCOUNT_AUTOCOMPLETE_TAG_FILTER_MODES: 'accountAutocompleteTagFilterModes',
    get TRACT_CRUISE_HIDDEN_COLUMNS () { return dynamicKeyGetter('tractCruiseHiddenColumns') },
    get TRACT_CRUISE_DEPLETION_HIDDEN_COLUMNS () { return dynamicKeyGetter('tractCruiseDepletionHiddenColumns') },
    get TRACT_CRUISE_UNIT_HIDDEN_COLUMNS () { return dynamicKeyGetter('tractCruiseUnitHiddenColumns') },
    get TICKET_GRID_HIDDEN_COLUMNS () { return dynamicKeyGetter('ticketGridHiddenColumns') },
    BP_TICKET_GRID_HIDDEN_COLUMNS: 'byProductTicketGridHiddenColumns',
    TRANSFER_TICKET_GRID_HIDDEN_COLUMNS: 'transferTicketGridHiddenColumns',
    LYS_TICKET_GRID_HIDDEN_COLUMNS: 'logYardSaleTicketGridHiddenColumns',
    get TRACT_GRID_HIDDEN_COLUMNS () { return dynamicKeyGetter('tractGridHiddenColumns') },
    PRODUCT_COSTS_DELIVERED_HIDDEN_COLUMNS: 'productCostsDeliveredHiddenColumns',
    PRODUCT_COSTS_STUMPAGE_HIDDEN_COLUMNS: 'productCostsStumpageHiddenColumns',
    PRODUCT_COSTS_HIDDEN_COLUMNS_BY_SUPPLIER: 'productCostsHiddenColumnsBySupplier',
    TICKET_PRODUCT_COSTS_HIDDEN_COLUMNS: 'ticketCostsHiddenColumns',
    STANDING_TIMBER_HEADERS: 'standingTimberHeaders',
    COMMITTED_TIMBER_HEADERS: 'committedTimberHeaders',
    TRACT_PRODUCT_COST_HIDDEN_COLUMNS: 'tractProductCostsHiddenColumns',
    LOGS_CONTRACT_GRID_HIDDEN_COLUMNS: 'logsContractGridHiddenColumns',
    BP_CONTRACT_GRID_HIDDEN_COLUMNS: 'byProductContractGridHiddenColumns',
    TRANSFERS_AND_LYS_CONTRACT_GRID_HIDDEN_COLUMNS: 'transfersContractGridHiddenColumns',
    get DASHBOARD_LOG_CHARTS () { return dynamicKeyGetter('dashboardLogCharts') },
    get DASHBOARD_BP_CHARTS () { return dynamicKeyGetter('dashboardBpCharts') },
    get DASHBOARD_OPERATIONAL_CHARTS () { return dynamicKeyGetter('dashboardOperationalCharts') },
    get DASHBOARD_BP_OPERATIONAL_CHARTS () { return dynamicKeyGetter('dashboardBpOperationalCharts') },
    DASHBOARD_HIDDEN_SECTIONS: 'dashboardHiddenSections',
    DASHBOARD_BP_HIDDEN_SECTIONS: 'dashboardBpHiddenSections',
    DASHBOARD_CONTRACT_MODE: 'contractMode',
    CONTRACT_FILTER_TYPES: 'contractFilterTypes',
    CONSULTING_FORESTER_ACCOUNT_AUTOCOMPLETE: 'consultingForesterAccountAutocomplete',
    CONSUME_GRID_HIDDEN_COLUMNS: 'consumeGridHiddenColumns',
    DELIVERED_WOOD_HIDDEN_COLUMNS: 'deliveredWoodHiddenColumns',
    TIMBER_PORTFOLIO_HIDDEN_COLUMNS: 'timberPortfolioHiddenColumns',
    get ACCOUNT_DETAIL_PAYMENTS_HEADERS () { return dynamicKeyGetter('accountDetailPaymentsHeaders') },
    get ACCOUNT_DETAIL_PAYMENT_TEMPLATE_HEADERS () { return dynamicKeyGetter('accountDetailPaymentTemplateHeaders') },
    DECK_GRID_HIDDEN_COLUMNS: 'deckGridHiddenColumns',
    get ACCOUNT_GRID_HIDDEN_COLUMNS () { return dynamicKeyGetter('accountGridHiddenColumns') },
    SPECIES_COSTS_DELIVERED_HIDDEN_COLUMNS: 'speciesCostsDeliveredHiddenColumns',
    SPECIES_COSTS_STUMPAGE_HIDDEN_COLUMNS: 'speciesCostsStumpageHiddenColumns',
    SPECIES_COSTS_HIDDEN_COLUMNS_BY_SUPPLIER: 'speciesCostsHiddenColumnsBySupplier',
    TRACT_SPECIES_COST_HIDDEN_COLUMNS: 'tractSpeciesCostsHiddenColumns',
    TRACT_GROUP_ANALYSIS_HIDDEN_COLUMNS: 'tractGroupAnalysisHiddenColumns',
    TRACT_GROUP_ANALYSIS_PRODUCT_HIDDEN_COLUMNS: 'tractGroupAnalysisProductHiddenColumns',
    SETTLEMENTS_BY_CONTRACT_HIDDEN_COLUMNS: 'settlementsByContractHiddenColumns',
    SETTLEMENTS_BY_CONTRACT_ACTIVITY_HIDDEN_COLUMNS: 'settlementsByContractActivityHiddenColumns',
    SETTLEMENTS_BY_CONTRACT_TICKET_HIDDEN_COLUMNS: 'settlementsByContractTicketHiddenColumns',
    SETTLEMENTS_BY_PAYEE_HIDDEN_COLUMNS: 'settlementsByPayeeHiddenColumns',
    SETTLEMENTS_BY_PAYEE_ACTIVITY_HIDDEN_COLUMNS: 'settlementsByPayeeActivityHiddenColumns',
    SETTLEMENTS_BY_PAYEE_TICKET_HIDDEN_COLUMNS: 'settlementsByPayeeTicketHiddenColumns',
    ACCRUALS_BY_PAYEE_HIDDEN_COLUMNS: 'accrualsByPayeeHiddenColumns',
    ACCRUALS_BY_PAYEE_ACTIVITY_HIDDEN_COLUMNS: 'accrualsByPayeeActivityHiddenColumns',
    ACCRUALS_BY_PAYEE_TICKET_HIDDEN_COLUMNS: 'accrualsByPayeeTicketHiddenColumns',
    BP_SETTLEMENTS_BY_PAYEE_HIDDEN_COLUMNS: 'byProductSettlementsByPayeeHiddenColumns',
    BP_SETTLEMENTS_BY_PAYEE_ACTIVITY_HIDDEN_COLUMNS: 'byProductSettlementsByPayeeActivityHiddenColumns',
    BP_SETTLEMENTS_BY_PAYEE_TICKET_HIDDEN_COLUMNS: 'byProductSettlementsByPayeeTicketHiddenColumns',
    BP_RECEIVABLES_BY_PAYER_HIDDEN_COLUMNS: 'byProductReceivablesByPayerHiddenColumns',
    BP_RECEIVABLES_BY_PAYER_ACTIVITY_HIDDEN_COLUMNS: 'byProductReceivablesByPayerActivityHiddenColumns',
    BP_RECEIVABLES_BY_PAYER_TICKET_HIDDEN_COLUMNS: 'byProductReceivablesByPayerTicketHiddenColumns',
    TICKET_REGISTER_TRACT_SUMMARY_HIDDEN_COLUMNS: 'ticketRegisterTractSummaryHiddenColumns',
    TICKET_REGISTER_CONTRACT_SUMMARY_HIDDEN_COLUMNS: 'ticketRegisterContractSummaryHiddenColumns',
    MISC_PAYMENTS_ADVANCES_HIDDEN_COLUMNS: 'miscPaymentsAdvancesHiddenColumns',
    MISC_PAYMENTS_TRACT_PAYMENTS_HIDDEN_COLUMNS: 'miscPaymentsTractPaymentsHiddenColumns',
    MISC_PAYMENTS_ACCOUNT_PAYMENTS_HIDDEN_COLUMNS: 'miscPaymentsAccountPaymentsHiddenColumns'
  }
}

export const SETTING_KEYS = getListOfSettingKeysByDeviceType(DeviceType.CURRENT)

export const ALL_SETTING_KEY_STRINGS = uniq([
  ...Object.values(getListOfSettingKeysByDeviceType(DeviceType.MOBILE)),
  ...Object.values(getListOfSettingKeysByDeviceType(DeviceType.DESKTOP))
])

export const ALL_WIDGET_SETTING_KEY_STRINGS = widgetMappings
  .filter(widget => !!widget.userSettingKey)
  .map(widget => widget.userSettingKey.value)
